import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface LeaderboardEntry {
  id: string;
  score: number;
  total_questions: number;
  percentage: number;
  completed_at: string;
  time_taken_seconds?: number;
  user_id?: string;
  session_id?: string;
  profiles?: {
    display_name?: string;
    username?: string;
  };
}

export const useQuizLeaderboard = (interviewId: string) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLeaderboard = async () => {
    if (!interviewId) return;

    try {
      setIsLoading(true);
      setError(null);

      // First get the quiz ID
      const { data: quiz, error: quizError } = await supabase
        .from('quizzes')
        .select('id')
        .eq('interview_id', interviewId)
        .single();

      if (quizError || !quiz) {
        return; // No quiz exists yet
      }

      // Get top 10 quiz attempts with user profiles
      const { data, error } = await supabase
        .from('quiz_attempts')
        .select(`
          id,
          score,
          total_questions,
          completed_at,
          time_taken_seconds,
          user_id,
          session_id
        `)
        .eq('quiz_id', quiz.id)
        .order('score', { ascending: false })
        .order('time_taken_seconds', { ascending: true, nullsFirst: false })
        .order('completed_at', { ascending: true })
        .limit(10);

      if (error) {
        throw error;
      }

      // Get user profiles separately to avoid relation issues
      const userIds = (data || []).map(attempt => attempt.user_id).filter(Boolean);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name, username')
        .in('user_id', userIds);

      // Create a map of user profiles
      const profileMap = (profiles || []).reduce((acc, profile) => {
        acc[profile.user_id] = profile;
        return acc;
      }, {} as Record<string, any>);

      // Calculate percentages and format data
      const formattedData: LeaderboardEntry[] = (data || []).map(attempt => ({
        id: attempt.id,
        score: attempt.score,
        total_questions: attempt.total_questions,
        percentage: Math.round((attempt.score / attempt.total_questions) * 100),
        completed_at: attempt.completed_at,
        time_taken_seconds: attempt.time_taken_seconds,
        user_id: attempt.user_id,
        session_id: attempt.session_id,
        profiles: attempt.user_id ? profileMap[attempt.user_id] : undefined
      }));

      setLeaderboard(formattedData);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch leaderboard');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [interviewId]);

  return {
    leaderboard,
    isLoading,
    error,
    refetch: fetchLeaderboard
  };
};